export enum ConfigType {
  BusinessHours = 'Business Hours',
  AIModel = 'AIModel',
  Inactivity = 'Inactivity',
  Contact = 'Contact',
  Moderation = 'Moderation',
  /** System-managed prompt-eval LLM-as-judge (read-only via API). */
  PromptEvalJudge = 'PromptEvalJudge',
  /** Project-scoped OpenAI key for prompt-eval bot + judge (managed via /api/prompt-eval/openai-config). */
  PromptEvalOpenAI = 'PromptEvalOpenAI'
}

export interface ContactConfig {
  companyName?: string;
  productName?: string;
  phone?: string;
  email?: string;
  address?: string;
  /** Backend-only for now; not shown in UI. Defaults to true. */
  liveChatFeatureActive?: boolean;
}

export interface ModerationConfig {
  /** When on: unrelated messages warn once, end on repeat, set status Warned. Default false. */
  offTopicModerationEnabled?: boolean;
  /** When on: insults warn once, end on repeat, set status Banned. Default false. */
  abusiveModerationEnabled?: boolean;
}

export type ModelRoutingTier = 'low' | 'medium' | 'high';

/** Auto-preferred catalog model for a routing tier (from GET /api/ai-models). */
export interface PreferredRoutingModel {
  aiModelId: number;
  externalId: string;
  name: string;
  routingTier?: ModelRoutingTier;
  /** List price USD per 1M input tokens */
  inputCostPer1m?: number;
  /** List price USD per 1M output tokens */
  outputCostPer1m?: number;
}

export interface AIProviderPreferredModels {
  low: PreferredRoutingModel | null;
  medium: PreferredRoutingModel | null;
  high: PreferredRoutingModel | null;
}

export interface AIModelConfig {
  key?: string;
  /** OpenAI org usage / admin API access; see OPENAI_ADMIN_KEY. Not used for chat completions. */
  admin_key?: string;
  /**
   * High-tier pin (optional). Catalog id from GET /api/ai-models → models[].aiModelId.
   * Set `null` to clear and use provider preferred high.
   */
  ai_model_id?: number | null;
  /**
   * Low-tier pin (optional). Set `null` to clear and use provider preferred low.
   */
  pin_low_ai_model_id?: number | null;
  /**
   * Medium-tier pin (optional). Set `null` to clear and use provider preferred medium.
   * PromptEval judge / classifier use medium resolve.
   */
  pin_medium_ai_model_id?: number | null;
  /** Denormalized provider code (filled from catalog when ai_model_id is set) */
  provider?: string;
  /** Denormalized external model id (legacy / filled from catalog when ai_model_id is set). Null clears. */
  model?: string | null;
  mock?: boolean;
  reasoning_effort?: string;
}

export interface AIModel {
  id: string;
  /** Catalog model id — prefer for config.ai_model_id / pin_* fields */
  aiModelId: number;
  name: string;
  description: string;
  reasoning: boolean;
  deprecated?: boolean;
  replacement?: string;
  replacementModelId?: number;
  shutdownDate?: string;
  routingTier?: ModelRoutingTier;
  /** List price USD per 1M input tokens */
  inputCostPer1m?: number;
  /** List price USD per 1M output tokens */
  outputCostPer1m?: number;
  isPreferredLow?: boolean;
  isPreferredMedium?: boolean;
  isPreferredHigh?: boolean;
}

export const REASONING_EFFORT_OPTIONS = [
  { value: 'low', label: 'Low', description: 'Faster, less reasoning' },
  { value: 'medium', label: 'Medium', description: 'Balanced speed and depth' },
  { value: 'high', label: 'High', description: 'Slower, more thorough reasoning' }
] as const;

export interface AIProviderWithModels {
  provider: string;
  providerId?: number;
  /** Daily-refreshed preferred models per routing tier (pin overrides these). */
  preferred?: AIProviderPreferredModels;
  models: AIModel[];
}

export type AIModelsResponse = AIProviderWithModels[];

export type AIModelWarningStatus = 'upcoming' | 'past';

export interface AIModelWarning {
  aiModelId: number;
  externalId: string;
  name: string;
  providerCode: string;
  shutdownDate: string;
  daysUntilShutdown: number;
  status: AIModelWarningStatus;
  replacementModelId?: number;
  replacementExternalId?: string;
  replacementName?: string;
  title: string;
  body: string;
  isActiveConfig: boolean;
}

export interface AIModelWarningsResponse {
  withinDays: number;
  asOf: string;
  activeConfig: {
    aiModelId: number | null;
    hasWarning: boolean;
    warning: AIModelWarning | null;
  };
  warnings: AIModelWarning[];
}

export interface ChatConfig {
  id: number | string;
  type: ConfigType;
  config: Record<string, any>;
  createdDate: string;
  updatedDate: string;
  deletedDate: string | null;
  /** Present on PromptEvalJudge config responses. */
  readOnly?: boolean;
  /** Enriched judge model on PromptEvalJudge (and similar) responses. */
  judgeModel?: {
    id?: number;
    aiModelId?: number;
    name?: string;
    externalId?: string;
    providerCode?: string;
  } | null;
}

export interface ChatConfigCreateRequest {
  type: ConfigType;
  config: Record<string, any>;
}

export interface ChatConfigUpdateRequest {
  type?: ConfigType;
  config?: Record<string, any>;
}

export interface ChatConfigListResponse {
  items: ChatConfig[];
  page: number;
  pageSize: number;
  total: number;
}

export interface BusinessHoursConfig {
  timezone: string;
  weekdays: {
    monday: {
      enable: boolean;
      start: string;
      end: string;
    };
    tuesday: {
      enable: boolean;
      start: string;
      end: string;
    };
    wednesday: {
      enable: boolean;
      start: string;
      end: string;
    };
    thursday: {
      enable: boolean;
      start: string;
      end: string;
    };
    friday: {
      enable: boolean;
      start: string;
      end: string;
    };
    saturday: {
      enable: boolean;
      start: string;
      end: string;
    };
    sunday: {
      enable: boolean;
      start: string;
      end: string;
    };
  };
  prompt: string;
}

